import { trackDesignSystemAction } from 'analytics';
import forge from 'apis/forge';
import ColorButton from 'components/Widget/Button/ColorButton';
import LoadingOverlay from 'components/Widget/Overlay/LoadingOverlay';
import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { selectOrganizations, selectUser, selectUserSettings } from 'redux/db/reducers';
import { RootReducer } from 'redux/reducers';
import { displayPromptModal } from 'redux/mode/util/modals';
import * as routes from 'routes';
import { SUPPORT_EMAIL, CALENDLY_URL } from '@constants';
import * as Base from '../styles';
import * as S from './styles';

const MAILTO_LINK = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
	'Design System Setup'
)}&body=${encodeURIComponent(
	"Hi — I'm interested in having Moonchild set up a design system for my team.\n\nCompany: \nWhat we're working with (Figma, code, etc.): \nTimeline: \n"
)}`;

const DesignSystemCreator = () => {
	const navigate = useNavigate();
	const [loadingMessage, setLoadingMessage] = useState<string>('');

	const { organizations, defaultOrganizationId, user } = useSelector(
		(r: RootReducer) => ({
			organizations: selectOrganizations(r.db),
			defaultOrganizationId: selectUserSettings(r.db).defaultOrganizationId,
			user: selectUser(r.db),
		}),
		shallowEqual
	);

	// Clear stale default org if it was deleted
	useEffect(() => {
		if (!defaultOrganizationId || !user || user.id === 'guest') return;
		if (organizations.length === 0) return;
		const orgExists = organizations.some((org) => org.id === defaultOrganizationId);
		if (!orgExists) {
			forge
				.updateUser({
					update: {
						id: user.id,
						metadata: {
							settings: { ...user.metadata?.settings, defaultOrganizationId: null },
						},
					},
				})
				.catch((err) => console.error('Failed to clear stale defaultOrganizationId:', err));
		}
	}, [organizations, defaultOrganizationId, user]);

	//----------------------------------------------------------------------
	// HANDLERS
	//----------------------------------------------------------------------

	useEffect(() => {
		trackDesignSystemAction('view_create_page');
	}, []);

	const onCreateBlankDs = async () => {
		try {
			setLoadingMessage('creating design system...');
			const res = await forge.createDesignSystem.withToast()({
				organizationId: defaultOrganizationId || undefined,
			});
			trackDesignSystemAction('created_blank_ds');
			setLoadingMessage('');
			navigate(routes.designSystem(res.designSystem.id, res.designSystem.title) + '?new=true');
		} catch {
			setLoadingMessage('');
		}
	};

	const onEmailUs = () => {
		trackDesignSystemAction('click_email_us');
		window.location.href = MAILTO_LINK;
	};

	const onBookCall = () => {
		trackDesignSystemAction('click_book_call');
		window.open(CALENDLY_URL, '_blank');
	};

	const onStartFromScratch = () => {
		trackDesignSystemAction('click_start_from_scratch');
		displayPromptModal({
			title: 'Fair warning',
			description:
				"Moonchild's DS editor is experimental. Powerful, but rough around the edges. If that's not your vibe, go back and let us set up your DS instead.",
			placeholder: 'Type "bold" to proceed',
			submitLabel: "Let's go",
			validate: (v: string) => (v.toLowerCase().trim() === 'bold' ? null : ' '),
			onSubmit: () => {
				trackDesignSystemAction('confirm_bold');
				onCreateBlankDs();
			},
		});
	};

	//----------------------------------------------------------------------
	// RENDERING
	//----------------------------------------------------------------------

	return (
		<Base.Base>
			{loadingMessage && <LoadingOverlay text={loadingMessage} semiTransparent />}
			<Base.Content>
				<S.PageContent>
					<S.Headline>Set up your design system</S.Headline>
					<S.Subheading>
						Every design system is different. Pick the path that fits your team.
					</S.Subheading>

					<S.CardGrid>
						<S.Card>
							<S.CardLabel>Recommended</S.CardLabel>
							<S.CardTitle>We'll import it for you</S.CardTitle>
							<S.CardDescription>
								Design systems take care to get right. Send us your Figma files, codebase, or
								product — we'll turn it into a polished, production-ready design system, typically
								in about a week.
							</S.CardDescription>
							<S.ButtonRow>
								<ColorButton label="Email us" onClick={onEmailUs} />
								<ColorButton label="Book a call" onClick={onBookCall} color="black" />
							</S.ButtonRow>
						</S.Card>

						<S.Card>
							<S.CardLabel>Experimental</S.CardLabel>
							<S.CardTitle>Build it yourself</S.CardTitle>
							<S.CardDescription>
								Use our AI-powered editor to import your design system or create one from scratch.
								It's powerful and getting better fast — but it's still early. Best for pioneers who
								like exploring new tools.
							</S.CardDescription>
							<S.ButtonRow>
								<ColorButton
									label="Start from scratch"
									onClick={onStartFromScratch}
									disabled={!!loadingMessage}
								/>
							</S.ButtonRow>
						</S.Card>
					</S.CardGrid>
				</S.PageContent>
			</Base.Content>
		</Base.Base>
	);
};

export default DesignSystemCreator;
